import React, { useState } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Header, Footer, SEO } from '../../../shared/components';

const APP_NAME = import.meta.env.VITE_APP_NAME === 'plotmagic' ? 'PlotMagic' : 'RRD Planners';
const LEGAL_NAME = 'Kuthati Sai Ram';
const ADDRESS = 'HNO 33-5-202, Satyasai Nagar, Opp Naidu Pump, Warangal, Telangana - 506002';
const EMAIL = 'rrdplotmagic@gmail.com';
const PHONE = '+91 9573766436';

const TermsPage: React.FC = () => {
  const [lang, setLang] = useState<'en' | 'te'>('en');

  return (
    <PageLayout size="full">
      <SEO title={`Terms & Conditions - ${APP_NAME}`} description="Terms and conditions for using our platform" />
      <Header />
      <PageLayout.Content>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">

            {/* Header + Language Toggle */}
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold text-foreground">
                {lang === 'en' ? 'Terms & Conditions' : 'నిబంధనలు & షరతులు'}
              </h1>
              <p className="text-muted-foreground">
                {lang === 'en' ? 'Last updated: April 2026' : 'చివరిగా నవీకరించబడింది: ఏప్రిల్ 2026'}
              </p>
              {/* Toggle */}
              <div className="inline-flex items-center rounded-full border border-border bg-muted p-1 gap-1">
                <button
                  onClick={() => setLang('en')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    lang === 'en'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setLang('te')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    lang === 'te'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  తెలుగు
                </button>
              </div>
            </div>

            <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">

              {/* 1. Introduction */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '1. Introduction' : '1. పరిచయం'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? <>This website is operated by <strong>{LEGAL_NAME}</strong>, located at {ADDRESS}. By accessing and using {APP_NAME} (the "Platform"), you accept and agree to be bound by these Terms & Conditions. If you do not agree, please do not use this service.</>
                    : <>ఈ వెబ్‌సైట్‌ను <strong>{LEGAL_NAME}</strong> నిర్వహిస్తుంది, చిరునామా: {ADDRESS}. {APP_NAME} ("ప్లాట్‌ఫారమ్") ని యాక్సెస్ చేయడం మరియు ఉపయోగించడం ద్వారా, మీరు ఈ నిబంధనలు & షరతులకు కట్టుబడి ఉంటారు. మీరు అంగీకరించకపోతే, దయచేసి ఈ సేవను ఉపయోగించవద్దు.</>}
                </p>
              </section>

              {/* 2. Services */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '2. Services' : '2. సేవలు'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? `${APP_NAME} is a credit-based SaaS platform that provides professional cadastral plot map generation services. Users can create, customize, and download A4 registration-ready plot maps as PDF documents. All services are digital in nature and delivered electronically.`
                    : `${APP_NAME} అనేది క్రెడిట్ ఆధారిత SaaS ప్లాట్‌ఫారమ్, ఇది వృత్తిపరమైన కేడాస్ట్రల్ ప్లాట్ మ్యాప్ జనరేషన్ సేవలను అందిస్తుంది. వినియోగదారులు A4 రిజిస్ట్రేషన్ రెడీ ప్లాట్ మ్యాప్‌లను PDF డాక్యుమెంట్లుగా రూపొందించవచ్చు, అనుకూలీకరించవచ్చు మరియు డౌన్‌లోడ్ చేసుకోవచ్చు. అన్ని సేవలు డిజిటల్ స్వభావం కలిగి ఉంటాయి మరియు ఎలక్ట్రానిక్‌గా అందజేయబడతాయి.`}
                </p>
              </section>

              {/* 3. User Accounts */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '3. User Accounts' : '3. వినియోగదారు ఖాతాలు'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? 'When you create an account, you must provide accurate and complete information. You are responsible for safeguarding your password and maintaining the confidentiality of your account. You agree to notify us immediately of any unauthorized use of your account.'
                    : 'మీరు ఖాతా సృష్టించేటప్పుడు, ఖచ్చితమైన మరియు పూర్తి సమాచారం అందించాలి. మీ పాస్‌వర్డ్‌ను రక్షించుకోవడం మరియు ఖాతా గోప్యతను నిర్వహించడం మీ బాధ్యత. ఖాతాకు అనధికారిక యాక్సెస్ జరిగినట్లయితే వెంటనే మాకు తెలియజేయాలని మీరు అంగీకరిస్తున్నారు.'}
                </p>
              </section>

              {/* 4. Credits & Payments */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '4. Credits & Payments' : '4. క్రెడిట్‌లు & చెల్లింపులు'}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {lang === 'en' ? 'Our platform operates on a credit-based system:' : 'మా ప్లాట్‌ఫారమ్ క్రెడిట్ ఆధారిత వ్యవస్థపై పని చేస్తుంది:'}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  {lang === 'en' ? <>
                    <li>Credits are purchased in packs at prices displayed in INR (Indian Rupees)</li>
                    <li>Credits are consumed when downloading PDF plot maps</li>
                    <li>First download of a design is charged at Tier 1 (new plan) rate</li>
                    <li>Subsequent downloads of the same design are charged at Tier 2 (edit) rate</li>
                    <li>All payments are processed securely via our payment gateway</li>
                    <li>Prices are inclusive of applicable taxes unless stated otherwise</li>
                  </> : <>
                    <li>క్రెడిట్‌లు INR (భారతీయ రూపాయలు) లో ప్రదర్శించబడిన ధరలతో ప్యాక్‌లలో కొనుగోలు చేయబడతాయి</li>
                    <li>PDF ప్లాట్ మ్యాప్‌లను డౌన్‌లోడ్ చేసేటప్పుడు క్రెడిట్‌లు వినియోగించబడతాయి</li>
                    <li>డిజైన్ మొదటి డౌన్‌లోడ్‌కు టైర్ 1 (నూతన ప్లాన్) రేటు వర్తిస్తుంది</li>
                    <li>అదే డిజైన్ తదుపరి డౌన్‌లోడ్‌లకు టైర్ 2 (సవరణ) రేటు వర్తిస్తుంది</li>
                    <li>అన్ని చెల్లింపులు మా పేమెంట్ గేట్‌వే ద్వారా సురక్షితంగా ప్రాసెస్ చేయబడతాయి</li>
                    <li>ముందుగా చెప్పనట్లయితే ధరలు వర్తించే పన్నులతో కూడిన ధరలు</li>
                  </>}
                </ul>
              </section>

              {/* 5. Use License */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '5. Use License' : '5. వినియోగ లైసెన్స్'}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {lang === 'en'
                    ? 'Upon purchasing credits and downloading plot maps, you are granted a non-exclusive, non-transferable license to use the generated PDFs for personal or professional purposes. You may not:'
                    : 'క్రెడిట్‌లు కొనుగోలు చేసి ప్లాట్ మ్యాప్‌లు డౌన్‌లోడ్ చేసిన తర్వాత, వ్యక్తిగత లేదా వృత్తిపరమైన అవసరాలకు PDF లను ఉపయోగించడానికి మీకు నాన్-ఎక్స్‌క్లూసివ్, నాన్-ట్రాన్స్‌ఫరబుల్ లైసెన్స్ మంజూరు చేయబడుతుంది. మీరు చేయకూడనివి:'}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  {lang === 'en' ? <>
                    <li>Resell, redistribute, or sublicense the platform or its generated outputs</li>
                    <li>Attempt to reverse engineer, decompile, or extract the source code</li>
                    <li>Use the service for any unlawful purpose</li>
                    <li>Share your account credentials with third parties</li>
                  </> : <>
                    <li>ప్లాట్‌ఫారమ్ లేదా దాని ఉత్పత్తులను తిరిగి విక్రయించడం, పంపిణీ చేయడం లేదా సబ్‌లైసెన్స్ చేయడం</li>
                    <li>సోర్స్ కోడ్‌ను రివర్స్ ఇంజినీర్ చేయడానికి, డీకంపైల్ చేయడానికి లేదా వేరు చేయడానికి ప్రయత్నించడం</li>
                    <li>చట్టవిరుద్ధమైన ఏ ప్రయోజనానికైనా సేవను ఉపయోగించడం</li>
                    <li>మూడవ పక్షాలతో మీ ఖాతా ఆధారాలు పంచుకోవడం</li>
                  </>}
                </ul>
              </section>

              {/* 6. Intellectual Property */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '6. Intellectual Property' : '6. మేధో సంపత్తి'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? `All content, features, and functionality of the Platform (excluding user-generated plot data) are owned by ${LEGAL_NAME} and are protected by copyright, trademark, and other intellectual property laws. The plot maps generated by users using their own data remain the property of the respective users.`
                    : `ప్లాట్‌ఫారమ్ యొక్క అన్ని కంటెంట్, ఫీచర్లు మరియు ఫంక్షనాలిటీ (వినియోగదారు రూపొందించిన ప్లాట్ డేటా మినహా) ${LEGAL_NAME} స్వంతమైనవి మరియు కాపీరైట్, ట్రేడ్‌మార్క్ మరియు ఇతర మేధో సంపత్తి చట్టాల ద్వారా రక్షించబడతాయి. వినియోగదారులు తమ సొంత డేటాతో రూపొందించిన ప్లాట్ మ్యాప్‌లు ఆయా వినియోగదారుల ఆస్తిగా ఉంటాయి.`}
                </p>
              </section>

              {/* 7. Disclaimer */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '7. Disclaimer' : '7. నిరాకరణ'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? 'The Platform is provided on an "as is" and "as available" basis. While we strive for accuracy, plot maps generated are for reference purposes and should be verified by a licensed surveyor before use in legal or official proceedings. We make no warranties regarding the accuracy, completeness, or suitability of the generated maps for any specific purpose.'
                    : 'ప్లాట్‌ఫారమ్ "యథాతథంగా" మరియు "అందుబాటులో ఉన్న విధంగా" అందించబడుతుంది. మేము ఖచ్చితత్వం కోసం ప్రయత్నిస్తున్నప్పటికీ, రూపొందించిన ప్లాట్ మ్యాప్‌లు సూచన ప్రయోజనాల కోసం మాత్రమే మరియు చట్టపరమైన లేదా అధికారిక విధానాల్లో ఉపయోగించే ముందు లైసెన్స్ పొందిన సర్వేయర్ ద్వారా ధృవీకరించబడాలి. ఏదైనా నిర్దిష్ట ప్రయోజనానికి రూపొందించిన మ్యాప్‌ల ఖచ్చితత్వం, సంపూర్ణత లేదా అనుకూలత గురించి మేము ఎటువంటి వారంటీలు ఇవ్వడం లేదు.'}
                </p>
              </section>

              {/* 8. Limitation of Liability */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '8. Limitation of Liability' : '8. బాధ్యత పరిమితి'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? `In no event shall ${LEGAL_NAME} be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of the Platform, including but not limited to errors in plot maps, loss of data, or business interruption.`
                    : `ప్లాట్ మ్యాప్‌లలో లోపాలు, డేటా నష్టం లేదా వ్యాపార అంతరాయంతో సహా ప్లాట్‌ఫారమ్ వినియోగం నుండి ఉత్పన్నమయ్యే ఏదైనా పరోక్ష, యాదృచ్ఛిక, ప్రత్యేక, పర్యవసాన లేదా శిక్షాత్మక నష్టాలకు ${LEGAL_NAME} బాధ్యత వహించదు.`}
                </p>
              </section>

              {/* 9. User Responsibility for Design Creations */}
              <section className="border-2 border-yellow-500/30 bg-yellow-500/5 rounded-xl p-6">
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '9. User Responsibility for Design Creations' : '9. డిజైన్ సృష్టులకు వినియోగదారు బాధ్యత'}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {lang === 'en'
                    ? 'All plot designs, layouts, and drawings created using the Platform are solely the responsibility of the user who created them. By using this Platform, you explicitly acknowledge and agree to the following:'
                    : 'ప్లాట్‌ఫారమ్ ఉపయోగించి రూపొందించిన అన్ని ప్లాట్ డిజైన్‌లు, లేఔట్‌లు మరియు డ్రాయింగ్‌లు వాటిని రూపొందించిన వినియోగదారు యొక్క పూర్తి బాధ్యత. ఈ ప్లాట్‌ఫారమ్ ఉపయోగించడం ద్వారా, మీరు కింది వాటిని స్పష్టంగా అంగీకరిస్తున్నారు:'}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4 mb-4">
                  {lang === 'en' ? <>
                    <li>You are solely responsible for the accuracy of all dimensions, measurements, and data you enter into the Platform.</li>
                    <li>Any errors, omissions, or inaccuracies in your designs are entirely your own responsibility.</li>
                    <li>The Platform is a design assistance tool only — it does not replace the judgment of a licensed surveyor, architect, or engineer.</li>
                    <li>Generated designs must not be used for legal registration, statutory approvals, or official submissions without independent professional verification.</li>
                    <li>You bear full responsibility for any consequences — legal, financial, or otherwise — arising from the use of designs created on this Platform.</li>
                  </> : <>
                    <li>మీరు ప్లాట్‌ఫారమ్‌లో నమోదు చేసిన అన్ని కొలతలు, మాపులు మరియు డేటా యొక్క ఖచ్చితత్వానికి మీరు మాత్రమే బాధ్యులు.</li>
                    <li>మీ డిజైన్‌లలో ఏదైనా లోపాలు, విస్మరణలు లేదా అఖచ్చితత్వాలు పూర్తిగా మీ బాధ్యత.</li>
                    <li>ప్లాట్‌ఫారమ్ కేవలం డిజైన్ సహాయక సాధనం మాత్రమే — ఇది లైసెన్స్ పొందిన సర్వేయర్, ఆర్కిటెక్ట్ లేదా ఇంజనీర్ నిర్ణయాన్ని భర్తీ చేయదు.</li>
                    <li>చట్టపరమైన నమోదు, శాసన ఆమోదాలు లేదా అధికారిక సమర్పణలకు స్వతంత్ర వృత్తిపరమైన ధృవీకరణ లేకుండా రూపొందించిన డిజైన్‌లు ఉపయోగించకూడదు.</li>
                    <li>ఈ ప్లాట్‌ఫారమ్‌లో రూపొందించిన డిజైన్‌ల ఉపయోగం వల్ల కలిగే ఏదైనా పరిణామాలకు — చట్టపరమైన, ఆర్థిక లేదా ఇతర — మీరు పూర్తి బాధ్యత వహిస్తారు.</li>
                  </>}
                </ul>
                <p className="text-muted-foreground leading-relaxed font-medium">
                  {lang === 'en'
                    ? `${LEGAL_NAME} holds no obligation, liability, or responsibility whatsoever for any mistakes, disputes, losses, or damages arising from designs created by users on this Platform.`
                    : `ఈ ప్లాట్‌ఫారమ్‌లో వినియోగదారులు రూపొందించిన డిజైన్‌ల వల్ల కలిగే ఏదైనా తప్పులు, వివాదాలు, నష్టాలు లేదా నష్టపరిహారాలకు ${LEGAL_NAME} ఎటువంటి బాధ్యత, దాయిత్వం లేదా జవాబుదారీతనం వహించదు.`}
                </p>
              </section>

              {/* 10. Termination */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '10. Termination' : '10. ముగింపు'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? 'We may terminate or suspend your account without prior notice if you breach these Terms. Upon termination, your right to use the Platform ceases immediately. Any unused credits at the time of termination due to breach may be forfeited.'
                    : 'మీరు ఈ నిబంధనలను ఉల్లంఘిస్తే మేము ముందస్తు నోటీసు లేకుండా మీ ఖాతాను ముగించవచ్చు లేదా సస్పెండ్ చేయవచ్చు. ముగింపు తర్వాత, ప్లాట్‌ఫారమ్ వినియోగించే మీ హక్కు వెంటనే నిలిపివేయబడుతుంది. ఉల్లంఘన కారణంగా ముగింపు సమయంలో ఉపయోగించని క్రెడిట్‌లు జప్తు చేయబడవచ్చు.'}
                </p>
              </section>

              {/* 11. Governing Law */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '11. Governing Law' : '11. పాలక చట్టం'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? 'These Terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Warangal, Telangana.'
                    : 'ఈ నిబంధనలు భారతదేశ చట్టాల ప్రకారం నిర్వహించబడతాయి మరియు అర్థం చేసుకోబడతాయి. ఏదైనా వివాదాలు వరంగల్, తెలంగాణలోని న్యాయస్థానాల ప్రత్యేక అధికార పరిధికి లోబడి ఉంటాయి.'}
                </p>
              </section>

              {/* 12. Changes to Terms */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '12. Changes to Terms' : '12. నిబంధనల మార్పులు'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {lang === 'en'
                    ? 'We reserve the right to modify these Terms at any time. Material changes will be communicated via email or an in-app notification at least 30 days before taking effect.'
                    : 'ఈ నిబంధనలను ఎప్పుడైనా మార్చుకునే హక్కు మాకు ఉంది. ముఖ్యమైన మార్పులు అమలులోకి రావడానికి కనీసం 30 రోజుల ముందు ఇమెయిల్ లేదా యాప్-లో నోటిఫికేషన్ ద్వారా తెలియజేయబడతాయి.'}
                </p>
              </section>

              {/* 13. Contact Information */}
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {lang === 'en' ? '13. Contact Information' : '13. సంప్రదింపు సమాచారం'}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>{LEGAL_NAME}</strong><br />
                  {ADDRESS}<br />
                  {lang === 'en' ? 'Email' : 'ఇమెయిల్'}: <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a><br />
                  {lang === 'en' ? 'Phone' : 'ఫోన్'}: <a href={`tel:${PHONE}`} className="text-primary hover:underline">{PHONE}</a>
                </p>
              </section>
            </div>

            <div className="text-center pt-8 border-t border-border">
              <a href="/" className="inline-flex items-center text-primary hover:underline font-medium">
                {lang === 'en' ? '← Back to Home' : '← హోమ్‌కు తిరిగి వెళ్ళు'}
              </a>
            </div>
          </div>
        </div>
      </PageLayout.Content>
      <Footer />
    </PageLayout>
  );
};

export default TermsPage;
